import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useNav } from '@/hooks';
import { Header, ScrollView } from '@/components';
import { ScrollViewRefAttrs } from '@/components/scroll-view';
import { getUrlParams } from '@/utils';
import { MassageType2Text } from '@/constant';
import service from '@/service';
import defaultAvatarImg from '@/assets/image/default-avatar.png';
import defaultFemaleAvatarImg from '@/assets/image/default-women-avatar.png';
import { ServiceDetailHeader } from './components/DetailHeader';
import { PhysiotherapyParameter } from './components/PhysiotherapyParameter';
import SpecificInfoRecord from './components/SpecificInfoRecord';
import CurrentChange from './components/CurrentChange';
import cx from './index.module.scss';

interface Props {}

export interface OneMoreOrderParams {
  orderId: string;
  massageItem: Massage.MassageItem;
  bodyPart: Service.BodyPart;
  storeGoodsId: number;
}

const ServiceDetail: React.FC<Props> = () => {
  const nav = useNav();

  const [serviceDetail, setServiceDetail] = useState<GetServiceOrderDetailResponse>({} as any);

  const [massageTaskInfo, setMassageTaskInfo] = useState<GetMassageTaskInfoResponse[]>([]);
  const [isGettingData, setIsGettingData] = useState(true);

  const [painLevelShockwave, setPainLevelShockwave] = useState<GetPainLevelResponse>(null);
  const [measurementDataRF, setMeasurementDataRF] = useState<GetMeasurementResultResponse>(null);
  const [questionnaireAnswer, setQuestionnaireAnswer] = useState<GetActionTestQuestionnaireResponse>({} as any);
  const curOrderId = useRef(null);
  const imagePreviewContainer = useRef(null);
  const outerScrollRef = useRef<ScrollViewRefAttrs>(null);
  const location = useLocation();
  const getMeasurementData = async orderId => {
    try {
      const { data } = await service.bodyMeasurementApi.getMeasurementResult(orderId);
      setMeasurementDataRF(data);
    } catch (e) {}
  };

  const getPainLevelData = async orderId => {
    try {
      const { data } = await service.actionTest.getPainLevel({ serviceOrderId: orderId });
      setPainLevelShockwave(data);
    } catch (e) {}
  };

  const getData = useCallback(async () => {
    try {
      const { orderId } = getUrlParams<{ orderId: string }>();
      curOrderId.current = orderId;
      // 获取服务记录
      const { data: serviceOrder } = await service.service.getServiceOrderDetail(orderId);

      if (!serviceOrder?.serviceOrderId) return;

      setServiceDetail(serviceOrder || ({} as any));

      if (serviceOrder?.itemType === 'Shockwave') {
        const { data: questionnaireAnswer } = await service.actionTest.getComparisonOfActionTest(orderId);
        setQuestionnaireAnswer(questionnaireAnswer);
        getPainLevelData(orderId);
      } else if (serviceOrder?.itemType === 'RadioFrequency') {
        getMeasurementData(orderId);
      }

      const { data: massageTaskInfo = [] } = await service.massage.getMassageTaskInfo(serviceOrder?.serviceOrderId);
      setMassageTaskInfo(massageTaskInfo);

      outerScrollRef?.current?.handleRefresh();
    } finally {
      setIsGettingData(false);
    }
  }, []);

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={cx('service-detail')} ref={imagePreviewContainer}>
      <Header
        title={
          <div className={cx('header-member-info')}>
            <img
              src={
                serviceDetail?.customerAvatar
                  ? serviceDetail?.customerAvatar
                  : serviceDetail?.customerGender === 'Female'
                  ? defaultFemaleAvatarImg
                  : defaultAvatarImg
              }
              alt="avatar"
              className={cx('img')}
              onError={e => ((e.target as HTMLImageElement).src = defaultAvatarImg)}
            />
            <span>{serviceDetail?.customerName}</span>
            <span style={{ color: 'rgba(0, 0, 0, 0.25)' }}>的{MassageType2Text[serviceDetail?.itemType]}服务详情</span>
          </div>
        }
        backConfig={{
          text: '会员详情',
          action: () =>
            nav('/user/member-detail', {
              state: { ...location?.state },
              replace: true,
            }),
        }}
      />
      <div className={cx('main-content')}>
        <ScrollView className={cx('right-box')} ref={outerScrollRef}>
          <div className={cx('right-box-inner')}>
            <div className={cx('order-info')}>
              <ServiceDetailHeader serviceDetail={serviceDetail} isGettingData={isGettingData} />
              <div className={cx('order-info-right')}>
                <PhysiotherapyParameter
                  serviceDetail={serviceDetail}
                  isGettingData={isGettingData}
                  massageTaskInfoList={massageTaskInfo}
                />

                {['Shockwave', 'RadioFrequency']?.includes(serviceDetail?.itemType) && (
                  <CurrentChange
                    serviceDetail={serviceDetail}
                    isGettingData={isGettingData}
                    measurementData={serviceDetail?.itemType === 'Shockwave' ? painLevelShockwave : measurementDataRF}
                  />
                )}
              </div>
            </div>
            <div className={cx('bottom')}>
              <SpecificInfoRecord
                serviceDetail={serviceDetail}
                isGettingData={isGettingData}
                measurementData={measurementDataRF}
                questionnaireAnswer={questionnaireAnswer}
                refresh={() => outerScrollRef.current?.handleRefresh()}
              />
            </div>
          </div>
        </ScrollView>
      </div>
    </div>
  );
};

export default ServiceDetail;
